import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchGetVerifiedBankAccounts } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { AddNewAccount } from './AddNewAccount';
import { PendingAccount } from './PendingAccount';
import { SelectVerifiedAccount } from './SelectVerifiedAccount';
import { VerifiedAccount } from './VerifiedAccount';
import { VerifyNewBankAccount } from './VerifyNewBankAccount';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import {
    TFundingRequirementErrors,
    TFundingRequirementStatus
} from '../../deserialiseFundingRequirements';

const UNABLE_TO_VERIFY_IBAN_ERROR_CODE = '280';

const OPEN_BANKING_ACCOUNT_NEEDED_ERROR_CODE = '278';

const MANUAL_BS_UPLOAD_NEEDED_ERROR_CODE = '277';

const errorCodesSetIBANVerificationNeeded = [
    UNABLE_TO_VERIFY_IBAN_ERROR_CODE,
    OPEN_BANKING_ACCOUNT_NEEDED_ERROR_CODE,
    MANUAL_BS_UPLOAD_NEEDED_ERROR_CODE
];

const bankVerificationRequired = (
    fundingRequirementErrors: TFundingRequirementErrors
) => {
    if (!fundingRequirementErrors || fundingRequirementErrors.length === 0) {
        return false;
    }

    const fundingStepErrorCodes = new Set(
        fundingRequirementErrors.map((err) => err.code)
    );

    return errorCodesSetIBANVerificationNeeded.every((errorCode) =>
        fundingStepErrorCodes.has(errorCode)
    );
};

export const BankDetailsActionDE = ({
    status,
    errors,
    maxFormalOfferAmount,
    triggerRefetchFundingRequirements
}: {
    status: TFundingRequirementStatus;
    errors: TFundingRequirementErrors;
    maxFormalOfferAmount: number | undefined;
    triggerRefetchFundingRequirements: () => void;
}) => {
    const stateKey = useStateKey();
    const { data: verifiedBankAccounts } = useQuery({
        queryKey: ['verified_bank_accounts', stateKey],
        queryFn: () => fetchGetVerifiedBankAccounts({ stateKey: stateKey }),
        suspense: true,
        select: (data) => data.data
    });

    useEffect(() => {
        tracking.ampli.addBankDetailsFundingStepShown({
            status: status,
            resolution_method_errors: errors?.map(
                (err) => `${err.code}: ${err.detail}`
            ),
            max_offered_amount: maxFormalOfferAmount
                ? maxFormalOfferAmount.toString()
                : ''
        });
    }, [maxFormalOfferAmount, status, errors]);

    if (status === 'PENDING') {
        return <PendingAccount />;
    }

    if (status === 'COMPLETED') {
        return <VerifiedAccount />;
    }

    if (bankVerificationRequired(errors)) {
        return (
            <VerifyNewBankAccount
                triggerRefetchFundingRequirements={
                    triggerRefetchFundingRequirements
                }
            />
        );
    }
    if (!verifiedBankAccounts || verifiedBankAccounts.length === 0) {
        return (
            <AddNewAccount
                triggerRefetchFundingRequirements={
                    triggerRefetchFundingRequirements
                }
            />
        );
    }

    return (
        <SelectVerifiedAccount
            verifiedBankAccounts={verifiedBankAccounts}
            triggerRefetchFundingRequirements={
                triggerRefetchFundingRequirements
            }
        />
    );
};
